import React, { useContext, useRef, useState } from 'react'
import moment from 'moment'
import { Button, ListMenu, ListMenuButton, Modal, Spacer, Spinner, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { Switch, useParams, useRouteMatch } from 'react-router-dom'
import CloseIcon from 'assets/icons/close'
import ScheduleIcon from 'assets/icons/schedule'
import uiConstants from 'config/constants/ui'
import InitiativeDeleteConfirm from 'src/components/Initiative/DeleteConfirm'
import ScheduleEditorSessionListItem from 'src/components/Schedule/EditorListItem'
import SessionCreate from 'src/components/Schedule/SessionCreate'
import SessionEdit from 'src/components/Schedule/SessionEdit'
import { useEventSessionDateGrouping } from 'src/components/utils'
import ModalRoute from 'src/components/utils/ModalRoute'
import { useEventSessionList } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'

const ScheduleEditor = ({ onCloseButtonClick }) => {
   const focusedSessionListItem = useRef()
   const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)

   const { initiative: event } = useContext(InitiativeHierarchyContext)
   const { sessionList, networkStatus } = useEventSessionList(event)
   const initialLoading = networkStatus <= 2

   // Because sessionList is already ordered, we do not need to worry about the order
   // of the day labels nor the order of sessions each day.
   const [sessionDates, sessionsGroupedByDate] = useEventSessionDateGrouping(sessionList)

   const match = useRouteMatch()
   const baseUrl = match.params.selectedDateKey
      ? match.url.split('/').slice(0, -1).join('/')
      : match.url

   const { selectedDateKey } = useParams()
   const includeDateInSessionListItems = !selectedDateKey || selectedDateKey === 'all'

   let selectedDateSessions = []
   if (networkStatus === 7) {
      try {
         selectedDateSessions = selectedDateKey && selectedDateKey !== 'all'
            ? sessionsGroupedByDate[selectedDateKey]
            : sessionList.items
      } catch (err) {} // eslint-disable-line no-empty
   }

   return (
      <View
         borderRadius="6px[c-f]"
         style={{ overflow: 'hidden' }}
         float="left"
         width="100%"
         height="100%">
         <View
            float="left"
            position="relative"
            width="100%[a-b] 240px[c-f]"
            height="100%"
            borderRight="1px solid #eee[c-f]"
            scroll="on">
            <View padding="18px[a-b] 30px[c-f]">
               <View
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="36px"
                  height="36px"
                  borderRadius="50%"
                  backgroundColor="#000">
                  <ScheduleIcon
                     width="20px"
                     height="20px"
                     strokeWidth="2px"
                     color="#fff"
                  />
               </View>
               <Spacer size="2" />
               <Text
                  size="5"
                  color="#000"
                  weight="medium">
                  {`${event.type.nameSingular} Schedule`}
               </Text>
               <Spacer size="2" />
               <Text size="2" color="#777">
                  {`Manage and create a schedule for this ${event.type.nameSingular}`}
               </Text>
               <Spacer size="5" />
               <Button
                  width="100%"
                  name="Add Schedule Item"
                  size="md"
                  color="#f7b77c"
                  textColor="#111"
                  linkTo={`${match.url}/create`}
               />
            </View>
            {sessionDates.length > 0 && (
               <ListMenu buttonPaddingHorizontal="18px[a-b] 30px[c-f]">
                  <ListMenuButton
                     linkTo={`${baseUrl}/all`}
                     name="All Schedule Items"
                  />
                  {sessionDates.map(sessionDate => (
                     <ListMenuButton
                        key={sessionDate.dateKey}
                        linkTo={`${baseUrl}/${sessionDate.dateKey}`}
                        name={sessionDate.nameFull}
                     />
                  ))}
               </ListMenu>
            )}
         </View>
         <View
            float="left"
            width="100%[a-b] calc(100% - 240px)[c-f]"
            height="100%">
            <View
               display="flex"
               justifyContent="flex-end"
               alignItems="center"
               float="left"
               width="100%"
               height="60px"
               padding="0px 18px"
               borderBottom="1px solid #eee">
               <View
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  onClick={onCloseButtonClick}>
                  <CloseIcon width="24px" height="24px" />
               </View>
            </View>
            <View
               float="left"
               width="100%"
               height="calc(100% - 60px)[c-f]"
               scroll="on">
               {initialLoading && (
                  <View width="100%" textAlign="center" padding="48px">
                     <Spinner color="#ccc" width="48px" height="48px" />
                  </View>
               )}
               {selectedDateSessions.length === 0 && (
                  <View textAlign="center" padding="48px">
                     <Text size="1.5">No schedule items have been created yet</Text>
                  </View>
               )}
               {selectedDateSessions.length > 0 && (
                  <View padding="12px 24px" backgroundColor="#f5f5f5">
                     <Text size="1.5" color="#888" weight="medium">
                        {`All times displayed in this schedule are in ${event.location.timezoneId} `}
                        {event.timezoneAbbr && `(${event.timezoneAbbr})`}
                     </Text>
                  </View>
               )}
               {selectedDateSessions.map((session, i) => (
                  <ScheduleEditorSessionListItem
                     key={session.id}
                     borderTop={i === 0 ? 'none' : undefined}
                     date={session.startDateTimeISO8601 && moment(session.startDateTimeISO8601).format('MMMM D')}
                     editUrl={`${baseUrl}/all/${session.slug}/edit`}
                     endTime={session.endTime}
                     includeDate={includeDateInSessionListItems}
                     location={session?.location?.name}
                     linkTo={`${baseUrl}/${selectedDateKey}/session/${session.slug}`}
                     onRemoveButtonClick={() => {
                        focusedSessionListItem.current = session
                        setDeleteModalIsOpen(true)
                     }}
                     name={session.name}
                     startTime={session.startTime}
                  />
               ))}
            </View>
         </View>
         <Switch>
            <ModalRoute path={`${match.path}/create`}>
               {({ open, onCloseComplete, onCloseTrigger }) => (
                  <Modal
                     borderRadius="6px"
                     width="270px"
                     onCloseComplete={onCloseComplete}
                     onCloseTrigger={onCloseTrigger}
                     open={open}
                     zIndex={uiConstants.zIndexes.tmpSettingsModal + 1}>
                     <SessionCreate
                        initiativeTypeId={event.type.id}
                        onCancelButtonClick={onCloseTrigger}
                        onCloseTrigger={onCloseTrigger}
                        parentInitiativeId={event.id}
                        onCreate={onCloseTrigger}
                     />
                  </Modal>
               )}
            </ModalRoute>
            <ModalRoute path={`${match.path}/:sessionSlug/edit`}>
               {({ open, onCloseComplete, onCloseTrigger }) => (
                  <Modal
                     borderRadius="6px"
                     width="100%[a-b] 90%[c] 480px[d-f]"
                     height="100%[a-b] 95%[c-f]"
                     onCloseTrigger={onCloseTrigger}
                     onCloseComplete={onCloseComplete}
                     open={open}
                     zIndex={uiConstants.zIndexes.tmpSettingsModal + 1}>
                     <SessionEdit onCloseButtonClick={onCloseTrigger} />
                  </Modal>
               )}
            </ModalRoute>
         </Switch>
         {/* Delete Initiative Confirmation Modal */}
         {!!focusedSessionListItem?.current && (
            <Modal
               borderRadius="6px"
               width="90%[a-b] 300px[c-f]"
               onCloseTrigger={() => setDeleteModalIsOpen(false)}
               open={deleteModalIsOpen}
               zIndex={uiConstants.zIndexes.tmpSettingsModal + 1}>
               <InitiativeDeleteConfirm
                  initiativeId={focusedSessionListItem.current.id}
                  initiativeName={focusedSessionListItem.current.name}
                  onCancelButtonClick={() => setDeleteModalIsOpen(false)}
                  onDelete={() => setDeleteModalIsOpen(false)}
               />
            </Modal>
         )}
      </View>
   )
}

ScheduleEditor.propTypes = {
   onCloseButtonClick: PropTypes.func.isRequired
}

export default ScheduleEditor
